import "./PanelDrawer.css";
import "animate.css";
import { useAppDispatch } from "../../app/store";
import {
  selectInscribedSubjects,
  selectSubjectsQuotas,
  selectPanelSubjectsDataList,
  setDetailSubjectOffered,
} from "../../reduxSlices/subjectsSlice";
import { IOfferedSubjectSection, IQuota } from "../../types";
import { useSelector } from "react-redux";
import { selectStudent } from "../../reduxSlices/studentSlice";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import ProfessorAndSubjectHours from "../ProfessorAndSubjectHours/ProfessorAndSubjectHours";
import QuotaCountIcon from "../QuotaCountIcon/QuotaCountIcon";
import ButtonInscription from "../ButtonInscription/ButtonInscription";
import { selectMobileFeatures } from "../../reduxSlices/globalFlagsSlice";
import ButtonCalendarize from "../ButtonCalendarize/ButtonCalendarize";
import ButtonPin from "../ButtonPin/ButtonPin";
import { extraerCodigoMateria, extraerNombreMateria, extraerSeccion, findProfeTitular, formatDateToDDMMYYYY } from "../../utils";
import { getCourseQuota } from "../../utils/quota";
import SlCopyButton from "@shoelace-style/shoelace/dist/react/copy-button";
import useCarreraType from "../../hooks/useCarreraType";
import { getCatalogoCondicionAprobCursoThunk } from "../../reduxAsyncThunks/subjects.thunks";
import { useEffect, useState } from "react";
import Loader from "../Loader/Loader";
import { useTranslatedText } from "../../contexts/TranslationContext";
import Translatable from "../Translatable/Translatable";

interface IPropsPanelDrawer {
  subject: IOfferedSubjectSection;
}

const PanelDrawer = ({ subject }: IPropsPanelDrawer) => {
  const dispatch = useAppDispatch();
  const subjectsQuotas = useSelector(selectSubjectsQuotas);
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const panelSubjectsDataList = useSelector(selectPanelSubjectsDataList);
  const { prioridad } = useSelector(selectStudent);
  const mobileFeatures = useSelector(selectMobileFeatures);
  const { esCarreraDeGrado } = useCarreraType();

  const [localCatalogo, setLocalCatalogo] = useState<string | null>(null);
  const [localCondicionAprob, setLocalCondicionAprob] = useState<string | null>(null);
  const [localLink, setLocalLink] = useState<string | null>(null);

  const [isLoadingCatalogo, setIsLoadingCatalogo] = useState(false);
  const [quota, setQuota] = useState<IQuota>(getCourseQuota(subjectsQuotas, subject.subjectData?.reserveData?.idC));
  const showCupoInfo =
    process.env.REACT_APP_ENV === "Test" ||
    process.env.REACT_APP_ENV === "Localhost" ||
    process.env.REACT_APP_ENV === "Staging" ||
    process.env.REACT_APP_ENV === "Dev";

  const esInscripta = inscribedSubjects?.filter(
    (curso) => curso.idCurso === subject.idCurso && curso.anioLectivo === subject.anioLectivo,
  ).length
    ? true
    : false;
  //const quota = getCourseQuota(subjectsQuotas, subject.subjectData?.reserveData?.idC);
  useEffect(() => {
    setQuota(getCourseQuota(subjectsQuotas, subject.subjectData?.reserveData?.idC));
  }, [subjectsQuotas, subject.subjectData?.reserveData?.idC]);

  const quotaQty = quota.qty;

  /* -------------------------------------------------------------------------- */
  /*                                  USE EFFECT                                 */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    // Buscar en el estado local primero
    const existingData = panelSubjectsDataList.find(
      (item) => item.idCurso === subject.idCurso && item.anioLectivo === subject.anioLectivo,
    );
    const { catalogo, condicionAprob, link } = existingData || {};

    if (existingData) {
      // Usar datos del estado local
      setLocalCatalogo(catalogo || null);
      setLocalCondicionAprob(condicionAprob || null);
      setLocalLink(link || null);

      setIsLoadingCatalogo(false);
    } else {
      // Hacer request para obtener los datos
      setIsLoadingCatalogo(true);
      const anioLectivo = subject.anioLectivo || new Date().getFullYear();
      const idAlumnoPrograma = subject.subjectData.reserveData.idA;

      dispatch(
        getCatalogoCondicionAprobCursoThunk({
          idCurso: subject.idCurso,
          anioLectivo,
          idAlumnoPrograma,
        }),
      ).finally(() => {
        setIsLoadingCatalogo(false);
      });
    }
  }, [subject.idCurso, panelSubjectsDataList, dispatch]);

  // Actualizar estado local cuando cambien los datos del panel
  useEffect(() => {
    const panelData = panelSubjectsDataList.find(
      (item) => item.idCurso === subject.idCurso && item.anioLectivo === subject.anioLectivo,
    );

    if (panelData) {
      setLocalCatalogo(panelData.catalogo);
      setLocalCondicionAprob(panelData.condicionAprob);
      setLocalLink(panelData.link);
      setIsLoadingCatalogo(false);
    }
  }, [panelSubjectsDataList, subject.idCurso]);

  /* -------------------------------------------------------------------------- */
  /*                                  HANDLERS                                  */
  /* -------------------------------------------------------------------------- */

  const handleClose = () => {
    dispatch(setDetailSubjectOffered(null));
  };

  const tagsListingRender = (tags: string) => {
    const colors = ["#C47DDD", "#2277DA", "#209DAE", "#97D86C", "#FBC54F", "#FF7A30", "#FF7A30"];
    const list = tags.split(",").map((item) => item.trim());

    return list.map((tag, i) => (
      <Translatable
        as="p"
        titleCase
        key={tag + i}
        style={{ background: colors[i < colors.length ? i : colors.length % i] }}
      >
        {tag}
      </Translatable>
    ));
  };

  const idCupoValid = subject.subjectData?.reserveData?.idC !== -1;

  const confirmedLabel = useTranslatedText("INSCRIPCIÓN CONFIRMADA");
  const loadingQuotasLabel = useTranslatedText("Cargando cupos");
  const loadingCourseInfoLabel = useTranslatedText("Cargando información del curso...");
  const approvalTitle = useTranslatedText("Condiciones de aprobación del Curso", { capitalize: true });
  const yearLabel = useTranslatedText("Año", { capitalize: true });
  const creditsLabel = useTranslatedText("Créditos", { capitalize: true });
  const shiftLabel = useTranslatedText("Turno", { capitalize: true });
  const modalityLabel = useTranslatedText("Modalidad", { capitalize: true });
  const datesLabel = useTranslatedText("Fechas", { capitalize: true });
  const minorFieldLabel = useTranslatedText("Campo menor", { capitalize: true });
  const observationsLabel = useTranslatedText("Observaciones", { capitalize: true });
  const descriptionTitle = useTranslatedText("Descripción del curso", { capitalize: true });
  const moreContentLabel = useTranslatedText("Ver más contenido sobre el curso", { capitalize: true });
  const subjectName = useTranslatedText(extraerNombreMateria(subject.section), { titleCase: true });
  const seccion = extraerSeccion(subject.section);
  const codigoMateria = extraerCodigoMateria(subject.section);
  const sectionName = seccion
    ? `${subjectName} - ${seccion}${codigoMateria ? ` ${codigoMateria}` : ""}`
    : subjectName;
  const copyLabel = useTranslatedText("Copiar");
  const copiedLabel = useTranslatedText("Copiado");
  const copyErrorLabel = useTranslatedText("Ups, no se pudo copiar");
  const modalidadValue = useTranslatedText(subject.modalidad ?? "");
  const turnoValue = useTranslatedText(subject.turno ?? "");
  const approvalText = useTranslatedText(localCondicionAprob ?? "");
  const observationsText = useTranslatedText(subject.obs ?? "");
  const catalogoText = useTranslatedText(localCatalogo ?? "");

  return (
    <>
      {!mobileFeatures && (
        <div className="absolute w-full h-screen top-0 left-0 overflow-hidden z-[60]" onClick={handleClose}></div>
      )}
      <div id="panel-drawer" className="desktop-panel-drawer animate__animated animate__slideInRight animate__faster">
        {/* ------------------------------ PANEL CONTENT ----------------------------- */}
        <div className="desktop-panel-drawer-content">
          {esInscripta && <h5 className="confirmed-subject-title">{confirmedLabel}</h5>}
          {mobileFeatures && prioridad && !esInscripta && idCupoValid && !quota.readyToUse && (
            <div className="flex items-center gap-1 mb-1">
              <p className="whitespace-nowrap text-[12px]" style={{ color: "#3b82f6" }}>
                {loadingQuotasLabel}
              </p>
              <div className="w-3 h-3 min-w-[12px] border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          )}
          <div className="panel-drawer-description-title mr-[30px]">
            {prioridad && !esInscripta && (quota.readyToUse || !idCupoValid) && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <QuotaCountIcon
                  qty={quotaQty}
                  subjectData={subject.subjectData}
                  availability={quota.availability}
                  idMateria={subject.idMateria}
                  quota={quota}
                  anioLectivo={subject.anioLectivo || ""}
                />
              </div>
            )}
            {!mobileFeatures && prioridad && !esInscripta && idCupoValid && !quota.readyToUse && (
              <div className="flex items-center justify-center gap-1">
                <p className="whitespace-nowrap text-sm" style={{ color: "#3b82f6" }}>
                  {loadingQuotasLabel}
                </p>
                <div className="w-4 h-4 border-[3px] border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
            )}
            <div className="panel-drawer-titles">
              <h2
                className="panel-drawer-title-name"
                title={
                  subject.subjectData.reserveData.tic && subject.subjectData.reserveData.tim && showCupoInfo
                    ? "Cupo: " +
                      String(subject.subjectData.reserveData.tic + " " + subject.subjectData.reserveData.tim) +
                      "   |   Cuota: " +
                      String(getCourseQuota(subjectsQuotas, subject.subjectData.reserveData.idC).qty || "-") +
                      `   |   idCurso:  ${subject.idCurso}` +
                      `   |   anioLectivo:  ${subject.anioLectivo}` +
                      `   |   idMateria:  ${subject.idMateria}` +
                      `   |   idCupo:  ${subject.subjectData.reserveData.idC}` +
                      `   |   anioLectivo:  ${subject.subjectData.reserveData.aL}`
                    : ""
                }
              >
                {sectionName}
                <span>
                  <SlCopyButton
                    style={{ padding: "0px", fontSize: "14px" }}
                    className="p-0 m-0"
                    value={sectionName}
                    copy-label={copyLabel}
                    success-label={copiedLabel}
                    error-label={copyErrorLabel}
                  />
                </span>
              </h2>
              <p className="panel-drawer-title-profesor">
                {findProfeTitular(subject.subjectData) ?? (
                  <Translatable as="span" capitalize>A designar</Translatable>
                )}
              </p>
            </div>
            <SlIcon
              name="x-lg"
              style={{ color: "black", minWidth: "20px", height: "20px", cursor: "pointer" }}
              onClick={handleClose}
            ></SlIcon>
          </div>
          <div className="drawe-content-scroll pr-[30px]">
            <ProfessorAndSubjectHours showBorderTop showRol subjectData={subject.subjectData} isDrawerContent={true} />

            {/* Loader para datos del catálogo */}
            {isLoadingCatalogo && (
              <div className="panel-drawer-content-rows">
                <Loader text={loadingCourseInfoLabel} />
              </div>
            )}

            {!isLoadingCatalogo && localCondicionAprob && localCondicionAprob.length > 0 && (
              <div className="panel-drawer-content-rows">
                <h3 className="panel-drawer-h3-title">{approvalTitle}</h3>
                <p>{approvalText}</p>
              </div>
            )}

            {(subject.year || subject.creditos || subject.modalidad) && (
              <div className="panel-drawer-content-rows row-modalidad">
                {subject.year && (
                  <div className="modalidad-inner-col">
                    <h4>{yearLabel}</h4>
                    <p>{subject.year}</p>
                  </div>
                )}
                {subject.creditos > 0 && (
                  <div className="modalidad-inner-col">
                    <h4>{creditsLabel}</h4>
                    <p>{subject.creditos}</p>
                  </div>
                )}
                {subject.turno && (
                  <div className="modalidad-inner-col">
                    <h4>{shiftLabel}</h4>
                    <p>{turnoValue}</p>
                  </div>
                )}
                {subject.modalidad && (
                  <div className="modalidad-inner-col">
                    <h4>{modalityLabel}</h4>
                    <p>{modalidadValue}</p>
                  </div>
                )}
                {!esCarreraDeGrado && subject?.infoFechas.fechaComienzo && subject.infoFechas.fechaFin && (
                  <div className="modalidad-inner-col">
                    <h4>{datesLabel}</h4>
                    <p>
                      <span className="whitespace-pre">{formatDateToDDMMYYYY(subject.infoFechas.fechaComienzo)} -</span>{" "}
                      {formatDateToDDMMYYYY(subject.infoFechas.fechaFin)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {subject.tag && (
              <div className="panel-drawer-content-rows">
                <h4>{minorFieldLabel}</h4>
                <div className="panel-tags-list-container"> {tagsListingRender(subject.tag)}</div>
              </div>
            )}

            {subject.obs && (
              <div className="panel-drawer-content-rows">
                <h4>{observationsLabel}</h4>
                <p>{observationsText}</p>
              </div>
            )}

            {!isLoadingCatalogo && localCatalogo && localCatalogo.length > 0 && (
              <div className="panel-drawer-content-rows">
                <h3 className="panel-drawer-h3-title">{descriptionTitle}</h3>
                <p className="panel-drawer-description-box">{catalogoText}</p>
                {localLink && (
                  <a className="panel-drawer-link" href={localLink} target="_blank">
                    {moreContentLabel}
                  </a>
                )}
              </div>
            )}
          </div>

          {!esInscripta && (
            <div id="row-actions">
              <div className="panel-actions-row mr-[30px]">
                <div className="panel-drawer-circle-buttons">
                  <ButtonPin subject={subject} />
                  <ButtonCalendarize subject={subject} />
                </div>
                <ButtonInscription
                  id={subject.idCurso}
                  idMateria={subject.idMateria}
                  subjectData={subject.subjectData}
                  quota={quota}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PanelDrawer;
